// src/features/ownerAdmin/GuildSearchInput.tsx
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api';
import { formatGuildName } from '../../lib/formatGuildName';

export interface GuildMatch {
  guild_id: string;
  guild_name: string | null;
  removed_at: string | null;
}

function useGuildSearch(query: string) {
  return useQuery({
    queryKey: ['creator-guild-search', query],
    queryFn: () => apiFetch<GuildMatch[]>(`/api/creator/guilds/search?q=${encodeURIComponent(query)}`),
    enabled: query.trim().length >= 1,
  });
}

export function GuildSearchInput({
  onSelect,
  selectedGuild,
}: {
  onSelect: (guild: GuildMatch | null) => void;
  selectedGuild: GuildMatch | null;
}) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const { data: matches, isLoading } = useGuildSearch(debouncedQuery);
  const showDropdown = debouncedQuery.trim().length >= 1 && !selectedGuild;

  return (
    <div className="item-search">
      <input
        className="input"
        type="text"
        value={selectedGuild ? formatGuildName(selectedGuild.guild_name, selectedGuild.guild_id) : query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (selectedGuild) onSelect(null);
        }}
        placeholder="Filter by server name or ID…"
      />
      {selectedGuild && (
        <button type="button" className="button-secondary" onClick={() => { onSelect(null); setQuery(''); }} style={{ marginLeft: '0.5rem' }}>
          Clear
        </button>
      )}
      {showDropdown && (
        <div className="item-search__dropdown">
          {isLoading && <p className="item-search__hint">Searching…</p>}
          {!isLoading && matches?.length === 0 && <p className="item-search__hint">No matching servers.</p>}
          {matches?.map((g) => (
            <button
              key={g.guild_id}
              type="button"
              className="item-search__result"
              onClick={() => { onSelect(g); setQuery(''); }}
            >
              {formatGuildName(g.guild_name, g.guild_id)} {g.removed_at && <span style={{ color: 'var(--negative)' }}>(bot removed)</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
